/**
 * Silent re-authentication for expired sessions.
 *
 * When a request comes back 401, callers ask `refreshSession()` to hit the
 * pinned refresh endpoint once. Concurrent 401s share a single in-flight
 * refresh (single-flight) instead of firing N parallel refresh calls.
 *
 * On unrecoverable failure (refresh itself 401s, 404s because the endpoint
 * isn't deployed yet, or the network is down), `redirectToReauth()` sends the
 * user back through the existing Google login flow with `return_url` set to
 * the page they were on, mirroring `useAuth`'s `signIn()`.
 */

const REFRESH_ENDPOINT = '/api/auth/refresh';

// A hung refresh call would stall every queued caller (single-flight)
// indefinitely, so the refresh request is bounded: past this timeout it is
// treated the same as any other refresh failure (fall back to re-login).
const REFRESH_TIMEOUT_MS = 10_000;

// Endpoints the interceptor must never itself intercept: intercepting the
// refresh call or the login/auth-kickoff routes would create a 401 retry loop.
const NON_INTERCEPTED_PATHS = [REFRESH_ENDPOINT, '/api/auth/google', '/api/auth/logout'];

export function isInterceptablePath(path: string): boolean {
  // Parse against the current origin so a future absolute URL (e.g. a
  // cross-subdomain API call) still resolves to a comparable pathname
  // instead of silently bypassing the exclusion list via a raw startsWith
  // on the full URL string.
  const { pathname } = new URL(path, window.location.origin);
  return !NON_INTERCEPTED_PATHS.some((skipped) => pathname.startsWith(skipped));
}

let inFlightRefresh: Promise<boolean> | null = null;

/**
 * Calls the refresh endpoint at most once per concurrent wave of 401s.
 * Returns true when the session was refreshed and the caller should retry
 * the original request; false when refresh failed and the caller should
 * fall back to `redirectToReauth()`.
 */
export function refreshSession(): Promise<boolean> {
  if (!inFlightRefresh) {
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

async function performRefresh(): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    const response = await fetch(REFRESH_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
    });
    // Any non-200 (401 session unrecoverable, 404 endpoint not deployed yet,
    // 5xx) is treated as refresh-unavailable: fall back to re-login.
    return response.ok;
  } catch {
    // Network failure, or the AbortController fired because the refresh
    // hung past REFRESH_TIMEOUT_MS: same fallback either way.
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Sends the user back through the existing Google login flow, preserving
 * the page they were on so they land back where they left off after
 * re-authenticating. Mirrors `useAuth`'s `signIn()`.
 */
export function redirectToReauth(): void {
  const returnUrl = window.location.origin + window.location.pathname + window.location.search;
  const returnParam = `?return_url=${encodeURIComponent(returnUrl)}`;
  window.location.href = `/api/auth/google${returnParam}`;
}

/**
 * Test-only hook to reset single-flight state between test cases.
 */
export function __resetSessionRefreshForTests(): void {
  inFlightRefresh = null;
}
